import applicationsApiService from "@/services/modules/applications";

import type { IUser } from "@/models/users";
import type { AssignableUser } from "@/models/users";

interface UserAssignmentConfig {
  apiField: string;
  mutationName: string;
  removeMutationName: string;
}

type UserAssignmentConfigMap = Record<AssignableUser, UserAssignmentConfig>;

export const USER_ASSIGNMENT_CONFIG: UserAssignmentConfigMap = {
  fundingAdvisor: {
    apiField: "funding_advisor_id",
    mutationName: "assignFundingAdvisor",
    removeMutationName: "removeFundingAdvisor",
  },
  clientFundingAdvisor: {
    apiField: "client_funding_advisor_id",
    mutationName: "assignClientFundingAdvisor",
    removeMutationName: "removeClientFundingAdvisor",
  },
  underwriter: {
    apiField: "underwriter_id",
    mutationName: "assignUnderwriter",
    removeMutationName: "removeUnderwriter",
  },
  clientUnderwriter: {
    apiField: "client_underwriter_id",
    mutationName: "assignClientUnderwriter",
    removeMutationName: "removeClientUnderwriter",
  },
  analyst: {
    apiField: "analyst_id",
    mutationName: "assignAnalyst",
    removeMutationName: "removeAnalyst",
  },
  clientAnalyst: {
    apiField: "client_analyst_id",
    mutationName: "assignClientAnalyst",
    removeMutationName: "removeClientAnalyst",
  },
};

export interface UserAssignmentPayload {
  appId: string;
  userId: number;
  user?: IUser;
  clientId?: string | number;
  removeDeal?: boolean;
}

export const getApiFieldForUserType = (userType: AssignableUser): string => {
  const config = USER_ASSIGNMENT_CONFIG[userType];
  return config?.apiField || "";
};

export const getMutationNameForUserType = (
  userType: AssignableUser
): string => {
  const config = USER_ASSIGNMENT_CONFIG[userType];
  return config?.mutationName || "";
};

export const getRemoveMutationNameForUserType = (
  userType: AssignableUser
): string => {
  const config = USER_ASSIGNMENT_CONFIG[userType];
  return config?.removeMutationName || "";
};

export const isClientUserType = (userType: AssignableUser): boolean => {
  return userType.startsWith("client");
};

export const createUserAssignmentAction = (userType: AssignableUser) => {
  return async (
    {
      commit,
    }: { commit: (mutation: string, payload: UserAssignmentPayload) => void },
    payload: UserAssignmentPayload
  ) => {
    const config = USER_ASSIGNMENT_CONFIG[userType];
    if (!config) {
      throw new Error(`Invalid user type: ${userType}`);
    }

    await applicationsApiService.assignUser(payload.appId, {
      [config.apiField]: payload.userId,
    });

    commit(config.mutationName, payload);
  };
};

export const createUserRemovalAction = (userType: AssignableUser) => {
  return async (
    { commit }: { commit: (mutation: string, appId: string) => void },
    appId: string
  ) => {
    const config = USER_ASSIGNMENT_CONFIG[userType];
    if (!config) {
      throw new Error(`Invalid user type: ${userType}`);
    }

    await applicationsApiService.assignUser(appId, {
      [config.apiField]: null,
    });

    commit(config.removeMutationName, appId);
  };
};
